import {
    useMemo,
    useCallback,
} from 'react'
import StatusHandler from 'app/shared-components/StatusHandler'
import Button from 'app/shared-components/Button'
import {
    ArchiveButton, useArchive, ArchiveConfirmationDialog,
} from 'app/shared-components/Archive'
import ConfirmationDialog from 'app/shared-components/ConfirmationDialog'
import useAddCopiedItemWorkaround from 'app/shared-components/SkyNetTable/hooks/useAddCopiedItemWorkaround'
import {
    useGetLocation,
} from 'app/Apps/ContactManagement/Locations/hooks'

import useRequestWithConfirmation from 'app/hooks/useRequestWithConfirmation'
import {
    useSkyNetDomainContext,
} from 'app/shared-components/SkyNetDomain'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import {
    LaneStatus,
} from 'app/enums/label.enums'
import useAccountManagementRoutes from 'app/SkyNetRoutes/hooks/useAccountManagementRoutes'
import request from 'app/requests/Lane/Lane.requests'
import laneStatusRequest from 'app/requests/Lane/LaneStatus.requests'
import {
    getReducedFormFields,
    useReducedFormStyles,
} from '../AccountLaneReducedForm'
import AccountLaneForm from '../AccountLaneForm/AccountLaneForm'
import useGetAccountLane from '../hooks/useGetAccountLane'

import {
    AccountLanesTable,
} from '../AccountLanes.types'

const defaultProps = {
    disabled: false,
}

export default function AccountLaneUpdate({
    id,
    disabled,
}: Readonly<{ id: number, disabled?: boolean }>) {
    const {
        setSelectedTab,
    } = useSkyNetDomainContext()
    const {
        Lanes: LanesRoutes,
    } = useAccountManagementRoutes()
    const {
        classes,
    } = useReducedFormStyles()
    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetAccountLane(id)

    const archive = useArchive({
        requestParamFn: data?.archived ? request.restore
            : request.archive,
        notificationId: 'LaneUpdateDto',
        title: 'Lane',
        data,
        redirectionAfterSuccess: generateSkyNetLink({
            domainPath: LanesRoutes.path,
        }),
    })

    const onCancelSuccess = useCallback(() => {
        invalidate()
        LanesRoutes.Update.navigate({
            params: {
                key: id,
            },
        })
    }, [
        invalidate,
        LanesRoutes.Update,
        id,
    ])

    const cancelLane = useRequestWithConfirmation({
        requestParamFn: laneStatusRequest.setAsCancelled,
        notificationId: 'LaneUpdateDto',
        title: 'Lane',
        data,
        actionDone: 'Cancelled',
        onSuccess: onCancelSuccess,
    })

    const {
        data: destinationAirportData,
    } = useGetLocation(data?.destinationAirport?.id)
    const {
        data: originAirportData,
    } = useGetLocation(data?.originAirport?.id)

    useAddCopiedItemWorkaround({
        data: {
            ...data,
            originAirport: originAirportData?.iataCode,
            destinationAirport: destinationAirportData?.iataCode,
            opportunity: '',
        } as AccountLanesTable,
    })
    const customButtons = useMemo(() => {
        return [
            <Button
                label="Copy"
                key="copy"
                name="copy"
                onClick={() => {
                    setSelectedTab(null)
                    LanesRoutes.Overview.navigate({
                        params: {
                            key: id,
                            copy: 'copy',
                        },
                    })
                }}
            />,
            [
                LaneStatus.TO_BE_QUOTED,
                LaneStatus.UNDER_REVIEW_PRICING,
                LaneStatus.REJECTED_PRICING,
            ].includes(data?.laneStatus)
            && (
                <Button
                    key="cancelLane"
                    label="Cancel Lane"
                    onClick={cancelLane.askConfirmation}
                />
            ),
            data?.laneStatus === LaneStatus.CANCELLED && (
                <ArchiveButton
                    onClick={archive.handleArchiveRestore}
                    archived={data?.archived}
                />
            ),
        ].filter(Boolean)
    }, [
        data?.laneStatus,
        data?.archived,
        cancelLane.askConfirmation,
        archive.handleArchiveRestore,
        setSelectedTab,
        LanesRoutes.Overview,
        id,
    ])

    const reducedMode = !disabled && data?.laneGroupId && [
        LaneStatus.IN_PROGRESS,
        LaneStatus.UNDER_REVIEW_PRICING,
        LaneStatus.PRICED,
        LaneStatus.AWARDED,
        LaneStatus.IMPLEMENTED,
    ].includes(data?.laneStatus)

    const requestParam = useMemo(() => {
        return reducedMode ? request.updateReduced : request.updateByAccount
    }, [reducedMode])

    const reducedFields = useMemo(() => {
        return reducedMode ? getReducedFormFields : undefined
    }, [reducedMode])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <AccountLaneForm
                    data={data}
                    requestParam={requestParam({
                        id,
                    })}
                    onSuccess={invalidate}
                    onCancel={invalidate}
                    disabled={disabled}
                    getCustomFields={reducedFields}
                    customButtons={customButtons}
                    customClasses={reducedMode ? classes : undefined}
                    exists
                />
                <ArchiveConfirmationDialog
                    title="Lane"
                    openConfirmDialog={archive.openConfirmDialog}
                    handleClose={archive.handleArchiveRestoreConfirm}
                    archived={data?.archived}
                />
                <ConfirmationDialog
                    open={cancelLane.openConfirmDialog}
                    handleClose={cancelLane.doAction}
                    dialogTitle="CANCEL LANE CONFIRMATION"
                    dialogContent="Do you want to cancel this lane?"
                />
            </>
        </StatusHandler>
    )
}

AccountLaneUpdate.defaultProps = defaultProps
